import React, { useImperativeHandle, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { getShapeChromeScreenRect } from '@/lib/editor/floating-canvas-chrome-bounds'
import {
  ToolbarGripButton,
  stopPropagation,
  useBlockBrowserZoomRef,
  useDraggableToolbarAnchor,
  useFloatingChromePosition,
} from '@/components/app/editor/tools/floating-toolbar'

import { DirectionToggle } from './components/direction-toggle'
import {
  TextOnPathColorControl,
  TextOnPathFontFamilyControl,
  TextOnPathFontSizeControl,
  TextOnPathFontStyleControl,
  TextOnPathOffsetControl,
  TextOnPathSpacingControl,
} from './components/format-controls'
import { SegmentSeam } from './components/segment-seam'
import { StartPositionControl } from './components/start-position-control'
import { TextCard } from './components/text-card'
import { SEGMENT_CLASS, SEGMENT_FIRST_CLASS, SEGMENT_LAST_CLASS } from './constants/segment-classes'
import { CARD_GAP, PILL_HEIGHT, TH, TW } from './constants/toolbar-dimensions'
import { createToolbarBlurHandler, markPointerDownInside } from './handlers/toolbar-blur'
import { useTextOnPathCharBudget } from './hooks/use-text-on-path-char-budget'
import { computeTextOnPathToolbarPos } from './helpers/text-on-path-toolbar-placement'
import type { PressOrigin } from './types/press-origin'
import type {
  TextOnPathToolbarProps,
  TextOnPathToolbarRef,
} from './types/text-on-path-toolbar-props'

export type {
  TextOnPathToolbarProps,
  TextOnPathToolbarRef,
} from './types/text-on-path-toolbar-props'

/**
 * Text on a Path action bar (WMPCM-9514, Story 1).
 *
 * A text card stacked over a control pill, portalled to `document.body` and smart-placed against
 * the shape — the same skeleton as `CropToolbar` / `NodeEditToolbar` / `CropZoomSlider`.
 *
 * Live: the text field and the Position control (Story 1); the Format Text controls — Font Family,
 * Style, Size, Color, Tracking and Kerning (WMPCM-9522, Story 2); Offset (WMPCM-9535, Story 4) —
 * all four live in `components/format-controls/`; and the direction flip (WMPCM-9530,
 * Story 3). Every control in the pill is now operable, so `PendingControl` is gone — nothing here
 * renders enabled-and-inert, which is this codebase's most-repeated defect (see
 * `larry-frontend-ui/CLAUDE.md` Known Gaps #22-31, #35, #37).
 */
export const TextOnPathToolbar = React.forwardRef<TextOnPathToolbarRef, TextOnPathToolbarProps>(
  (
    {
      shape,
      scale,
      canvasRef,
      draft,
      pathLength,
      onTextChange,
      onStartDistanceChange,
      onConfigChange,
      onDismiss,
    },
    ref
  ) => {
    // Held here rather than in `StartPositionControl`: toggling it re-renders this root, which
    // re-reads the canvas rect for a manually-placed bar, exactly as before the split.
    const [positionOpen, setPositionOpen] = useState(false)
    const textareaRef = useRef<HTMLTextAreaElement | null>(null)
    const pressOriginRef = useRef<PressOrigin>('none')
    const charBudget = useTextOnPathCharBudget(draft, pathLength)
    const { rootRef, setRootRef } = useBlockBrowserZoomRef()
    const pos = useFloatingChromePosition(canvasRef, shape, scale, computeTextOnPathToolbarPos)
    const {
      displayPos,
      isDragging,
      handleDragStart,
      handleDragMove,
      handleDragEnd,
      cancelActiveDrag,
    } = useDraggableToolbarAnchor({
      shape,
      scale,
      canvasRef,
      pos,
      width: TW,
      height: TH,
      getShapeRect: getShapeChromeScreenRect,
      revertOnCancel: true,
    })

    useImperativeHandle(ref, () => ({ cancelActiveDrag }), [cancelActiveDrag])

    if (!displayPos) return null

    // Handed to every Format Text control: closing one puts the caret back in the text field
    // rather than leaving it parked on the trigger button.
    const restoreTextFocus = () => textareaRef.current?.focus()

    return createPortal(
      <div
        ref={setRootRef}
        // Every menu/popover surface that must not blur an active canvas text-edit session carries
        // this. Radix portals its own content to document.body, so the Position popover below has
        // to be marked separately — an attribute here does not reach it.
        data-text-edit-keepalive=""
        data-text-on-path-toolbar=""
        style={{ position: 'fixed', left: displayPos.x, top: displayPos.y, zIndex: 9999 }}
        onPointerDownCapture={(e) =>
          markPointerDownInside(pressOriginRef, e.target, rootRef.current)
        }
        onPointerDown={stopPropagation}
        onPointerUp={stopPropagation}
        onClick={stopPropagation}
        onBlur={createToolbarBlurHandler(rootRef, onDismiss, pressOriginRef, restoreTextFocus)}
      >
        {/*
          `items-center`: the design centres the 500px text field on the bar rather than making
          the two flush — see WMPCM-8692, "Textarea input has been fixed across the design so it
          is center aligned with the text on a path toolbar".
        */}
        <div className="flex flex-col items-center" style={{ width: TW, gap: CARD_GAP }}>
          <TextCard
            textareaRef={textareaRef}
            text={draft.text}
            charBudget={charBudget}
            onTextChange={onTextChange}
          />

          {/*
            Control pill — the "Format Text panel" of AC-3.

            `gap-0` and `p-1` are the design's own (node 20416:459700). `w-max` lets it hug like
            the design's bar does, and `max-w-full` keeps a very long font-family name compressing
            inside the column rather than overflowing it and throwing the placement clamps off by
            half the overflow.
          */}
          <div
            className="flex w-max max-w-full items-center gap-0 rounded-md border border-border bg-popover p-1 shadow-md"
            style={{ height: PILL_HEIGHT }}
          >
            <ToolbarGripButton
              variant="compact"
              aria-label="Move toolbar"
              isDragging={isDragging}
              onDragStart={handleDragStart}
              onDragMove={handleDragMove}
              onDragEnd={handleDragEnd}
            />

            {/*
              No standalone divider after the grip. The design's bar is `gap-0` with no separator
              node — the hard line the eye reads next to the grabber is the segmented group's own
              left border sitting flush against it. The `-ml-1` hairline that used to be here
              floated in an 8px gap at 12% black and so read as absent; putting it back on top of
              the group border would only double the line.

              Font Family / Style / Size / More read as ONE visually joined segmented group —
              WMPCM-8692's acceptance criteria. `h-8` makes the wrapper exactly 32px including its
              borders (`box-sizing: border-box`); the children fill it and square their seams via
              the `SEGMENT_*` classes (`constants/segment-classes.ts`).
            */}
            <div className="flex h-8 flex-shrink-0 items-center rounded-md border border-input bg-background">
              <TextOnPathFontFamilyControl
                config={draft}
                onConfigChange={onConfigChange}
                onRestoreFocus={restoreTextFocus}
                className={SEGMENT_FIRST_CLASS}
              />
              <SegmentSeam />
              <TextOnPathFontStyleControl
                config={draft}
                onConfigChange={onConfigChange}
                onRestoreFocus={restoreTextFocus}
                className={SEGMENT_CLASS}
              />
              <SegmentSeam />
              <TextOnPathFontSizeControl
                config={draft}
                onConfigChange={onConfigChange}
                onRestoreFocus={restoreTextFocus}
                className={SEGMENT_CLASS}
              />
              <SegmentSeam />
              {/* AC-6's "Slot": Kerning and Tracking share one control behind this trigger. */}
              <TextOnPathSpacingControl
                config={draft}
                onConfigChange={onConfigChange}
                onRestoreFocus={restoreTextFocus}
                className={SEGMENT_LAST_CLASS}
              />
            </div>

            <TextOnPathColorControl
              config={draft}
              onConfigChange={onConfigChange}
              onRestoreFocus={restoreTextFocus}
            />

            {/*
              Offset (WMPCM-9535) and Start Position (AC-4) share a second segmented group, per
              WMPCM-8692's design review: "in the toolbar for offset and starting space buttons,
              make the button group with straight edge in the middle to match button group for
              font options". Node 20416:459700 still draws these two ungrouped — this is a
              deliberate divergence from the node, following the newer review comment, not an
              oversight.
            */}
            <div className="flex h-8 flex-shrink-0 items-center rounded-md border border-input bg-background">
              <TextOnPathOffsetControl
                config={draft}
                onConfigChange={onConfigChange}
                onRestoreFocus={restoreTextFocus}
                className={SEGMENT_FIRST_CLASS}
              />
              <SegmentSeam />
              <StartPositionControl
                open={positionOpen}
                onOpenChange={setPositionOpen}
                startDistance={draft.startDistance}
                pathLength={pathLength}
                onStartDistanceChange={onStartDistanceChange}
              />
            </div>

            <DirectionToggle
              clockwise={draft.clockwise}
              onConfigChange={onConfigChange}
              onRestoreFocus={restoreTextFocus}
            />
          </div>
        </div>
      </div>,
      document.body
    )
  }
)

TextOnPathToolbar.displayName = 'TextOnPathToolbar'

/*
 * `PendingControl` was removed in WMPCM-9530 (Story 3). It rendered the last unimplemented slot
 * visibly disabled — the direction flip — and that was the only caller left, so with the flip now
 * operable every control in the pill does something. Recover it from git history if a future story
 * needs to stage a slot again; the pattern (render disabled, never enabled-and-inert) is still the
 * right one.
 */
